import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { MuiBox, MuiBackdrop, MuiTypography } from "..";
import {
  loaderSelector,
  siteCoreContentLoadingSelector,
} from "../../redux/slices/appState/appStateSelector";
import LoaderImage from "../../assets/images/loader.gif";
import { LoaderWrapper } from "./Loader.style";
import { useSiteCore } from "../../hooks/useSiteCore";

const useDebouncedVisibility = (visible: boolean, delay = 200) => {
  const [debouncedVisible, setDebouncedVisible] = useState(visible);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (visible) {
      setDebouncedVisible(true);
    } else {
      timeout = setTimeout(() => setDebouncedVisible(false), delay);
    }
    return () => clearTimeout(timeout);
  }, [visible, delay]);

  return debouncedVisible;
};

export const Loader: FC = () => {
  const { commonLabels } = useSiteCore();
  const loaders = useSelector(loaderSelector);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const isGlobalVisible = loaders.global?.isVisible || siteCoreLoading;
  const globalMessage = loaders.global?.message || sessionStorage.getItem("loadingMessage") || commonLabels.loadingPleaseWait;

  const activeLoader = Object.entries(loaders)
    .filter(([key, loader]) => key !== "global" && loader.isVisible)
    .map(([_, loader]) => loader)[0];

  const debouncedVisible = useDebouncedVisibility(
    isGlobalVisible || !!activeLoader
  );

  return (
    <LoaderWrapper role="progressbar" aria-label="Loader">
      {debouncedVisible && (
        <MuiBackdrop data-testid="global-loader" open={true} aria-live="polite" role="status">
          <MuiBox className="backdrop-container"></MuiBox>
          <MuiBox className="loading-container" aria-busy={!debouncedVisible} tabIndex={0}>
            <img src={LoaderImage} alt="Loading..." className="loader"   />
            {globalMessage && (
              <MuiTypography variant="h3" className="loading-text">
                {globalMessage}
              </MuiTypography>
            )}
          </MuiBox>
        </MuiBackdrop>
      )}
    </LoaderWrapper>
  );
};
